import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { X, Loader2, Globe, Key, Settings, Code } from "lucide-react";
import { toast } from "react-hot-toast";
import Input from "../../widgets/Input";
import CustomDropdownSelect from "../../widgets/CustomDropdownSelect";
import {
  Webhook,
  CreateWebhookData,
  UpdateWebhookData,
  WEBHOOK_METHODS,
  API_KEY_FORMATS,
  TriggerEventOption,
} from "../../services/webhooks.service";
import webhooksService from "../../services/webhooks.service";
import Tooltip from "../../widgets/ToolTip";

interface WebhookFormProps {
  isOpen: boolean;
  onClose: () => void;
  webhook: Webhook | null;
  onSubmit: (data: CreateWebhookData | UpdateWebhookData) => Promise<void>;
  isLoading: boolean;
}

export default function WebhookForm({
  isOpen,
  onClose,
  webhook,
  onSubmit,
  isLoading,
}: WebhookFormProps) {
  const { control, handleSubmit, reset } = useForm<CreateWebhookData>();

  const [triggerEventOptions, setTriggerEventOptions] = useState<
    TriggerEventOption[]
  >([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const isEdit = !!webhook;

  useEffect(() => {
    if (isOpen) {
      fetchTriggerEventOptions();

      if (webhook) {
        reset({
          webhook_name: webhook.webhook_name,
          webhook_description: webhook.webhook_description,
          webhook_trigger_events: webhook.webhook_trigger_events || [],
          webhook_method: webhook.webhook_method,
          webhook_url: webhook.webhook_url,
          webhook_api_key_format: webhook.webhook_api_key_format,
          webhook_api_key: webhook.webhook_api_key,
          webhook_api_value: webhook.webhook_api_value,
          is_active: webhook.is_active,
        });
      } else {
        reset({
          webhook_name: "",
          webhook_description: "",
          webhook_trigger_events: [],
          webhook_method: "POST",
          webhook_url: "",
          webhook_api_key_format: "HEADER",
          webhook_api_key: "",
          webhook_api_value: "",
          is_active: true,
        });
      }
    }
  }, [isOpen, webhook, reset]);

  const fetchTriggerEventOptions = async () => {
    try {
      setIsLoadingOptions(true);
      const options = await webhooksService.getTriggerEventOptions();
      setTriggerEventOptions(options);
    } catch (error) {
      console.error("Error fetching trigger event options:", error);
      toast.error("Failed to load trigger event options");
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const handleFormSubmit = (data: CreateWebhookData) => {
    // Validate trigger events
    if (
      !data.webhook_trigger_events ||
      data.webhook_trigger_events.length === 0
    ) {
      toast.error("Please select at least one trigger event");
      return;
    }

    onSubmit(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? "Edit Webhook" : "Create New Webhook"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isLoading}
          >
            <Tooltip content="Close">
            <X className="w-5 h-5" />
            </Tooltip>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
        <form
          className="p-6 space-y-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Basic Information
              </h3>
            </div>

            <Input
              name="webhook_name"
              control={control}
              label="Webhook Name"
              placeholder="e.g., Document Processing Webhook"
              rules={{ required: "Webhook name is required" }}
            />

            <Input
              name="webhook_description"
              control={control}
              label="Description"
              placeholder="Describe what this webhook does..."
              rules={{ required: "Description is required" }}
            />
          </div>

          {/* Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Code className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Configuration
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Trigger Events Multiselect */}
              <CustomDropdownSelect
                name="webhook_trigger_events"
                control={control as any}
                label="Trigger Events"
                placeholder="Select trigger events"
                options={triggerEventOptions}
                isMulti={true}
                isLoading={isLoadingOptions}
                allowClear={true}
                rules={{ required: "At least one trigger event is required" }}
              />

              {/* HTTP Method Dropdown */}
              <CustomDropdownSelect
                name="webhook_method"
                control={control as any}
                label="HTTP Method"
                placeholder="Select HTTP method"
                options={WEBHOOK_METHODS.map((method) => ({
                  value: method,
                  label: method,
                }))}
                rules={{ required: "HTTP method is required" }}
              />
            </div>
          </div>

          {/* Endpoint Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Endpoint Information
              </h3>
            </div>

            <Input
              name="webhook_url"
              control={control}
              label="Webhook URL"
              placeholder="https://your-api.com/webhook"
              rules={{
                required: "Webhook URL is required",
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: "Please enter a valid URL",
                },
              }}
            />
          </div>

          {/* Authentication */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Authentication
              </h3>
            </div>

            <CustomDropdownSelect
              name="webhook_api_key_format"
              control={control as any}
              label="API Key Format"
              placeholder="Select API key format"
              options={API_KEY_FORMATS.map((format) => ({
                value: format,
                label: format.charAt(0) + format.slice(1).toLowerCase(),
              }))}
              rules={{ required: "API key format is required" }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                name="webhook_api_key"
                control={control}
                label="API Key Name"
                placeholder="e.g., x-api-key, Authorization"
                rules={{ required: "API key name is required" }}
              />

              <Input
                name="webhook_api_value"
                control={control}
                label="API Key Value"
                type="password"
                placeholder="Your API key value"
                rules={{ required: "API key value is required" }}
              />
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              {...(control as any).register("is_active")}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Enable this webhook
            </label>
          </div>

        </form>
        </div>
          {/* Form Actions */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white z-10">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              onSubmit={handleSubmit(handleFormSubmit)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isEdit ? "Updating..." : "Creating..."}</span>
                </>
              ) : (
                <span>{isEdit ? "Update Webhook" : "Create Webhook"}</span>
              )}
            </button>
          </div>
      </div>
    </div>
  );
}
